import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import CompanyFooter from '../components/CompanyFooter';
import { BoatIcon, ErgIcon, BOAT_TYPES } from '../components/BoatIcons';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LoginPage() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Check if already authenticated
    fetch(`${API}/auth/me`, { credentials: 'include' })
      .then(res => {
        if (res.ok) {
          return res.json().then(userData => {
            setUser(userData);
            setIsAuthenticated(true);
            setChecking(false);
          });
        }
        setChecking(false);
      })
      .catch(() => setChecking(false));
  }, []);

  const handleLogin = () => {
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleBoatSelect = async (boatId) => {
    // Save boat type to localStorage and backend
    localStorage.setItem('default_boat_type', boatId);
    
    try {
      await fetch(`${API}/user/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ default_boat_type: boatId })
      });
    } catch (error) {
      console.error('Failed to save boat type:', error);
    }

    // If ERG is selected, go directly to Settings page to connect PM5
    if (boatId === 'c2_erg') {
      toast.info('Go to Settings to connect your PM5');
      navigate('/settings');
      return;
    }

    navigateToNextPage();
  };
  
  const navigateToNextPage = () => {
    // Check if profile is complete
    const profileComplete = user?.dob && user?.sex && user?.height && user?.weight;
    
    if (profileComplete) {
      navigate('/dashboard');
    } else {
      navigate('/profile-setup');
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-zinc-900 transition-colors">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-zinc-900 dark:border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-zinc-900 flex flex-col transition-colors">
      {/* Header with Logo */}
      <div className="pt-8 pb-4 px-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/crewz-logo-light.png" alt="CrewZ" className="h-12 w-auto dark:hidden" data-testid="login-crewz-logo" />
            <img src="/crewz-logo-dark.png" alt="CrewZ" className="h-12 w-auto hidden dark:block" data-testid="login-crewz-logo-dark" />
          </div>
          
          {/* User Profile Picture (if authenticated) */}
          {isAuthenticated && user && (
            <div className="flex items-center gap-3">
              {user.picture ? (
                <img 
                  src={user.picture} 
                  alt={user.name} 
                  className="w-10 h-10 rounded-full border-2 border-zinc-200 dark:border-zinc-700"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-zinc-200 dark:bg-zinc-700 flex items-center justify-center text-zinc-600 dark:text-zinc-300 font-bold">
                  {user.name?.charAt(0) || user.email?.charAt(0) || '?'}
                </div>
              )}
              <span className="text-sm font-medium text-zinc-600 dark:text-zinc-300 hidden sm:block">{user.name || user.email}</span>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 pb-8">
        {!isAuthenticated ? (
          /* Login Screen - Clean and Simple */
          <div className="text-center space-y-8 max-w-md">
            <div>
              <h1 className="text-4xl md:text-5xl font-extrabold tracking-tighter mb-4 text-zinc-900 dark:text-white" style={{ fontFamily: 'Archivo, sans-serif' }}>
                Welcome
              </h1>
              <p className="text-lg text-zinc-500">Sign in to start tracking</p>
            </div>
            
            <button
              onClick={handleLogin}
              data-testid="login-button"
              className="w-full px-8 py-4 text-lg font-bold uppercase tracking-wider bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 transition-colors"
              style={{ fontFamily: 'Archivo, sans-serif' }}
            >
              Sign In with Google
            </button>
          </div>
        ) : (
          /* Boat Selection Screen */
          <div className="w-full max-w-4xl">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-extrabold tracking-tighter mb-2" style={{ fontFamily: 'Archivo, sans-serif' }}>
                Select Your Session
              </h2>
              <p className="text-zinc-500">Choose your boat class or ergometer</p>
            </div>

            {/* Boat Selection Grid - 10 items (9 boats + ERG) */}
            <div className="grid grid-cols-5 md:grid-cols-10 gap-2">
              {BOAT_TYPES.map((boat) => (
                <button
                  key={boat.id}
                  onClick={() => handleBoatSelect(boat.id)}
                  data-testid={`boat-${boat.id}`}
                  className="group flex flex-col items-center p-3 rounded-xl border-2 border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 hover:border-zinc-900 dark:hover:border-zinc-500 hover:shadow-lg transition-all duration-200 active:scale-95"
                >
                  <div className="w-14 h-18 md:w-16 md:h-20 text-zinc-700 dark:text-zinc-300 group-hover:text-zinc-900 dark:group-hover:text-white transition-colors mb-1">
                    {boat.isErg ? (
                      <ErgIcon />
                    ) : (
                      <BoatIcon rowers={boat.rowers} isSculling={boat.isSculling} showCox={boat.showCox} />
                    )}
                  </div>
                  <span className="text-sm md:text-base font-bold text-zinc-900 dark:text-white" style={{ fontFamily: 'Archivo, sans-serif' }}>
                    {boat.name}
                  </span>
                  <span className="text-[10px] text-zinc-400 hidden md:block truncate w-full text-center">
                    {boat.fullName}
                  </span>
                </button>
              ))}
            </div>

            {/* Divider with "OR" */}
            <div className="flex items-center my-8">
              <div className="flex-1 border-t border-zinc-200"></div>
              <span className="px-4 text-sm text-zinc-400 uppercase tracking-wider">or</span>
              <div className="flex-1 border-t border-zinc-200"></div>
            </div>

            {/* Direct to Monitor — defaults to single scull (1x) if user skips selection */}
            <div className="text-center">
              <button
                onClick={() => handleBoatSelect('1x')}
                data-testid="continue-without-selecting"
                className="text-zinc-500 hover:text-zinc-900 font-medium underline transition-colors"
              >
                Continue without selecting
              </button>
            </div>
          </div>
        )}
      </div>
      <CompanyFooter />
    </div>
  );
}
